import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowUp, Check, ChevronDown, Heart } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { authStore } from "../lib/authStore";
import { analytics } from "../lib/analytics";
import { useCart } from "../lib/cart";
import { formatAZN, pickLocale } from "../lib/format";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import ProductCard, { explodeColourCards, normalizeProduct } from "../components/storefront/ProductCard";
import AdfImage from "../components/storefront/AdfImage";
import TryOnBadge, { TryOnIcon } from "../components/storefront/TryOnBadge";
import { TryOnModal } from "../components/atelier/TryOnModal";
import { ConciergeModal } from "../components/atelier/ConciergeModal";
import { AtelierModal } from "../components/atelier/AtelierModal";
import { SmallCaps } from "../components/atelier/primitives";
import { localProductImages } from "../components/storefront/assets";
import { DELIVERY, RETURNS, policyText } from "../content/policies";
import SeoMeta from "../components/storefront/SeoMeta";
import GalleryLightbox from "../components/storefront/GalleryLightbox";
import SectionBoundary from "../components/layout/SectionBoundary";
import NotFound from "./NotFound";
import { useI18n } from "../lib/i18n";
import { apiErrorMessage } from "../lib/apiErrors";
import { useContactDetails } from "../hooks/useSiteContent";

/** Rəsmi WhatsApp qlifi — lucide brend loqoları daşımır, danışıq balonu isə
 *  düyməni "adi mesaj" kimi oxutdururdu. */
function WhatsAppIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.172-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

export default function PDP() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { locale, t } = useI18n();
  const { addItem } = useCart() || {};
  // Same WhatsApp number the Contact page and concierge use, admin-managed —
  // powers the "Bizə mesaj göndər" action beside the buy button.
  const contactDetails = useContactDetails() || {};
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);
  const [completeLook, setCompleteLook] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  // option id → chosen option-value id; the variant is derived from the pair.
  const [chosen, setChosen] = useState({});
  const [adding, setAdding] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingWardrobe, setSavingWardrobe] = useState(false);
  const [sizeError, setSizeError] = useState(false);
  const [error, setError] = useState("");
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [reservationBusy, setReservationBusy] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  // M5 has shipped for months — engine, per-category charts, seeds and approved
  // copy — and the only caller in the repo was the admin size-chart tester. The
  // customer typed their height and weight into the account and nothing on the
  // storefront ever asked for the answer.
  const [sizeHint, setSizeHint] = useState(null);
  const [jsonLd, setJsonLd] = useState(null);
  const [lightbox, setLightbox] = useState(null); // index of the image to open on
  const [swipeIndex, setSwipeIndex] = useState(0);
  // Səbətə düşən variant — düymə "Səbətdə"yə çevrilir, toast/yönləndirmə yox.
  const [addedVariantId, setAddedVariantId] = useState(null);
  // CTA seçimsiz basılanda ayrıca ekran açmırıq: seçim bölməsinə sürüşüb
  // onu vizual işıqlandırırıq.
  const [optionsPulse, setOptionsPulse] = useState(false);
  const optionsRef = useRef(null);
  const galleryTrackRef = useRef(null);
  const [sizeSheetOpen, setSizeSheetOpen] = useState(false);
  const [tryOnConfig, setTryOnConfig] = useState(null);
  const [galleryTryOnVisible, setGalleryTryOnVisible] = useState(false);
  const galleryRailRef = useRef(null);

  useEffect(() => {
    let active = true;
    api.get(`/products/${handle}`)
      .then((r) => {
        if (!active) return;
        setProduct(r.data);
        // Sizing is a deliberate choice in tailoring: never pre-select a size,
        // or a shopper can order one they never actually looked at. An option
        // with a single value is not a choice though — most of this catalogue
        // comes in one colour, and asking someone to press the only swatch
        // before the button will even name the real decision is pure friction.
        const preset = {};
        const pickable = (r.data?.options || []).filter((option) => option?.values?.length);
        pickable.forEach((option) => {
          const values = option.values || [];
          if (values.length === 1 && !isSizeOption(option)) preset[option.id] = values[0].id;
        });
        /* Rəng-ayrı-kartdan gələn keçid: ?color=<value|id> həmin rəngi
           seçilmiş edir — qalereya, ölçülər, stok və try-on o rəngin
           kontekstində açılır (sahibin spesifikasiyası, 2026-09-24). */
        const wantedColour = new URLSearchParams(window.location.search).get("color");
        if (wantedColour) {
          const colourOption = pickable.find((option) => isColourOption(option));
          const match = (colourOption?.values || []).find(
            (value) => value.id === wantedColour
              || (value.value || "").toLowerCase() === wantedColour.toLowerCase(),
          );
          if (colourOption && match) preset[colourOption.id] = match.id;
        }
        // A preset that answers every question must also pick the variant.
        // Filling `chosen` while leaving selectedVariant null made the CTA
        // fall back to "Select size" on a page with no size row, and the
        // click died in silence — most of this catalogue is single-colour,
        // so that dead button was the rule, not the edge case.
        const presetComplete = pickable.every((option) => preset[option.id]);
        setSelectedVariant(
          presetComplete
            ? (r.data?.variants || []).find((variant) => {
                const held = new Set(variant.option_values || []);
                return pickable.every((option) => held.has(preset[option.id]));
              }) || null
            : null,
        );
        setChosen(preset);
        setSaved(false);
        setSizeError(false);
        analytics.viewItem(r.data);
      })
      .catch(() => {
        if (!active) return;
        // A failed load is a failed load. Substituting a bundled placeholder
        // put an invented product, price and all, behind a real URL.
        setNotFound(true);
      });
    return () => { active = false; };
  }, [handle]);

  // The mobile gallery is a horizontal snap track (see index.css). Report which
  // slide is centred so the dots underneath mean something.
  useEffect(() => {
    const track = galleryTrackRef.current;
    if (!track) return undefined;
    const onScroll = () => {
      if (!track.clientWidth) return;
      setSwipeIndex(Math.round(track.scrollLeft / track.clientWidth));
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, [handle]);

  // Product + Offer + BreadcrumbList, built server-side from live inventory.
  // Two requests rather than one because the breadcrumb node is a separate
  // endpoint; both are cheap, cached by the browser, and neither blocks paint.
  useEffect(() => {
    if (!handle) { setJsonLd(null); return undefined; }
    let active = true;
    Promise.allSettled([
      api.get(`/seo/product/${handle}/jsonld`),
      api.get(`/seo/breadcrumb/product/${handle}`),
    ]).then(([productNode, breadcrumbNode]) => {
      if (!active) return;
      const nodes = [];
      if (productNode.status === "fulfilled" && productNode.value?.data) nodes.push(productNode.value.data);
      if (breadcrumbNode.status === "fulfilled" && breadcrumbNode.value?.data) nodes.push(breadcrumbNode.value.data);
      setJsonLd(nodes.length ? nodes : null);
    });
    return () => { active = false; };
  }, [handle]);

  // Only for a signed-in customer: the engine prefills height, weight, body
  // type and preferred fit from the profile, so an anonymous call has nothing
  // to reason from. A miss stays silent — the engine now reports when it cannot
  // place a body confidently, and a guessed size is worse than no size.
  useEffect(() => {
    if (!handle || !authStore.getAccessToken()) { setSizeHint(null); return undefined; }
    let active = true;
    api.post("/size-recommendation", { product_handle: handle, fit: "classic" })
      .then((r) => { if (active) setSizeHint(r.data?.matched ? r.data : null); })
      .catch(() => { if (active) setSizeHint(null); });
    return () => { active = false; };
  }, [handle]);

  useEffect(() => {
    let active = true;
    api.get("/products", { params: { page: 1, page_size: 8, sort: "newest" } })
      .then((r) => { if (active) setRelated(r.data.items || []); })
      .catch(() => { if (active) setRelated([]); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    api.get("/tryon/config")
      .then((r) => { if (active) setTryOnConfig(r.data); })
      .catch(() => { if (active) setTryOnConfig({ enabled: false, provider_ready: false }); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!handle) return;
    let active = true;
    api.get(`/products/${handle}/complete-the-look`)
      .then((r) => { if (active) setCompleteLook(r.data); })
      .catch(() => { if (active) setCompleteLook(null); });
    return () => { active = false; };
  }, [handle]);

  /* Rəng seçimi URL-ə yazılır (?color=<value>), silinəndə çıxır — browser
     back/forward rəng kontexti ilə birgə işləyir. `replace` yox: hər seçim
     tarixçə addımıdır. Dövr yaranmır — URL-dən oxuma yalnız yüklənmə
     presetindədir. */
  useEffect(() => {
    if (!product) return;
    const colourOption = (product.options || []).find(isColourOption);
    if (!colourOption) return;
    const chosenId = chosen[colourOption.id];
    const chosenValue = (colourOption.values || []).find((value) => value.id === chosenId);
    const current = searchParams.get("color") || "";
    const next = chosenValue ? chosenValue.value : "";
    if ((current || "") === (next || "")) return;
    const params = new URLSearchParams(searchParams);
    if (next) params.set("color", next); else params.delete("color");
    setSearchParams(params, { replace: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen, product]);

  // The colour the shopper has picked, if any — the gallery follows it.
  const chosenColourValueId = useMemo(() => {
    const colour = (product?.options || []).find(isColourOption);
    return colour ? chosen[colour.id] : undefined;
  }, [product, chosen]);

  const images = useMemo(() => {
    const all = (product?.images || []).filter((image) => isUsableImage(image.url));
    // Show only the chosen colour's photography. A piece whose shots are not
    // tagged by colour keeps showing all of them rather than going blank.
    const forColour = chosenColourValueId
      ? all.filter((image) => image.option_value_id === chosenColourValueId)
      : [];
    const shared = chosenColourValueId
      ? all.filter((image) => !image.option_value_id)
      : [];
    // Colour-specific shots lead the gallery, while shared detail/lifestyle
    // photography remains visible for every colour.
    const shown = (forColour.length ? [...forColour, ...shared] : all).map((image) => mediaUrl(image.url));
    if (shown.length) return shown;
    if (isUsableImage(product?.thumbnail_url)) return [mediaUrl(product.thumbnail_url)];
    // Fotosuz məhsul: 4 demo slayd yox, tək neytral brend lövhəsi.
    return localProductImages(0).slice(0, 1).map((image) => image.src);
  }, [product, chosenColourValueId]);

  // The styling edit repeated at the top of the buy column: it used to live
  // only in a section below the fold, so the shopper had to scroll past the
  // whole spec sheet to learn the piece had companions at all.
  const lookChips = useMemo(() => {
    const curatedRows = flattenCuratedSlots(completeLook);
    const source = curatedRows.length ? curatedRows : related;
    const seen = new Set();
    return source
      .filter((row) => row.handle && row.handle !== handle && row.id && !seen.has(row.id))
      .map((row) => {
        seen.add(row.id);
        return {
          id: row.id,
          handle: row.handle,
          image: mediaUrl(row.thumbnail_url),
          label: slotLabel(row.slot, locale) || pickLocale(row.category?.title, locale) || pickLocale(row.title, locale),
        };
      })
      .filter((row) => isUsableImage(row.image))
      .slice(0, 5);
  }, [completeLook, related, handle, locale]);

  // Bayraq + hər hansı şəkil: qarment ayrıca seçilməyibsə server ilk yüklənən
  // şəkli qarment kimi götürür; aksesuar/ayaqqabıda bayraq default söndürülür.
  // try_on_ui serverdə hesablanır: bayraq aktiv + kateqoriya kökü
  // ayaqqabı/aksesuar deyil (onlarda düymə heç vaxt görünmür — sahibin
  // qərarı, 2026-09-24). Köhnə cavab üçün bayrağın özü ehtiyatdır.
  const aiEnabled = Boolean(
    product && (product.try_on_ui ?? product.ai_try_on_enabled)
    && (product.ai_garment_asset || product.thumbnail_url || images[0]),
  );

  useEffect(() => {
    const gallery = galleryRailRef.current;
    if (!aiEnabled || !gallery) {
      setGalleryTryOnVisible(false);
      return undefined;
    }
    if (typeof IntersectionObserver === "undefined") {
      setGalleryTryOnVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setGalleryTryOnVisible(entry.isIntersecting),
      { rootMargin: "-109px 0px -76px 0px", threshold: 0 },
    );
    observer.observe(gallery);
    return () => observer.disconnect();
  }, [aiEnabled, handle]);

  useEffect(() => () => {
    if (avatarPreview?.startsWith("blob:")) URL.revokeObjectURL(avatarPreview);
  }, [avatarPreview]);

  if (notFound) return <NotFound />;

  if (!product) {
    return (
      <div className="min-h-screen bg-[var(--adf-cream)] text-[var(--adf-ink)]">
        <Header />
        <div className="adf-container py-28 font-serif text-3xl text-[var(--adf-gray-2)]">Yüklənir…</div>
      </div>
    );
  }

  const item = normalizeProduct(product);
  const title = pickLocale(item.title, locale);
  const subtitle = pickLocale(product.subtitle, locale);
  const whatsappDigits = String(contactDetails.whatsapp || "").replace(/\D/g, "");
  const whatsappHref = whatsappDigits
    ? `https://wa.me/${whatsappDigits}?text=${encodeURIComponent(`${t("pdp.whatsapp_prefill", "Salam! Bu məhsul haqqında məlumat almaq istəyirəm:")} ${title}`)}`
    : null;
  const description = pickLocale(product.description, locale);
  // No invented copy: neither source feed writes product prose, and a generic
  // sentence printed under every piece reads as filler even when it is flattering.
  const story = pickLocale(product.story, locale);
  const atelierNote = pickLocale(product.atelier_note, locale);
  // Once a variant is settled the page quotes that variant. Quoting the
  // product's minimum forever meant picking a dearer colour or size left the
  // cheapest price on screen right up to the basket.
  const price = formatAZN(selectedVariant?.price ?? product.min_price, locale);
  // Catalogue rows carry a was-price, and until now nothing on the storefront
  // ever printed it — a piece marked down 25% looked like a full-price piece.
  // The quote follows the same variant the price does.
  const priceMinor = selectedVariant?.price ?? product.min_price;
  const compareSource = selectedVariant
    || (product.variants || []).find((variant) => variant.price === product.min_price);
  const compareMinor = compareSource?.compare_at_minor || 0;
  const onSale = compareMinor > priceMinor;
  const discountPct = onSale ? Math.round((1 - priceMinor / compareMinor) * 100) : 0;
  // A product with no variants used to advertise 48/50/52/54, none of which
  // existed — the basket then rejected every one of them.
  const variants = product.variants || [];

  // Colour is picked before size in a fitting room, so the size row goes last.
  const pickerOptions = (product.options || [])
    .filter((option) => option.values?.length)
    .slice()
    .sort((a, b) => Number(isSizeOption(a)) - Number(isSizeOption(b)))
    /* The payload carries `position` but does not sort by it — values come back
       in insertion order, so a size added to an existing product landed wherever
       it was created and the row read 46, 50, 52, 48. Order by the field that
       exists to express order. */
    .map((option) => ({
      ...option,
      values: (option.values || [])
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    }));

  /* What an option value is called on the shop.
     `value` is the raw key stored on the option row — "Denim", "Beyaz" — and it
     is not always something to show a customer. `label` is the name an editor
     gives it in Rəng və ölçülər, and it was being written and then never read:
     every picker printed the raw key, so renaming a colour in admin changed
     nothing on the page. */
  const valueLabel = (value) => pickLocale(value?.label, locale) || value?.value || "";

  const matchesChosen = (variant, selection, ignoreOptionId) => {
    const held = new Set(variant.option_values || []);
    return pickerOptions.every((option) => {
      if (option.id === ignoreOptionId) return true;
      const wanted = selection[option.id];
      return !wanted || held.has(wanted);
    });
  };

  /** Whether this value is combinable with what is already chosen, and in stock. */
  const valueAvailability = (option, value) => {
    const candidates = variants.filter(
      (variant) => (variant.option_values || []).includes(value.id)
        && matchesChosen(variant, chosen, option.id),
    );
    return {
      exists: candidates.length > 0,
      inStock: candidates.some((v) => v.stock > 0 || v.allow_backorder || v.manage_inventory === false),
    };
  };

  const chooseValue = (optionId, valueId) => {
    // Re-picking the same value clears it, so a shopper can back out of a
    // combination that leaves nothing selectable in the other row — EXCEPT a
    // single-value option (a one-colour piece). That value is preset and
    // required; letting a click toggle it off stranded the shopper with the
    // colour deselected, the button stuck on "Rəng seç" and no way to add.
    const opt = pickerOptions.find((o) => o.id === optionId);
    const onlyValue = (opt?.values || []).length === 1;
    const clearing = chosen[optionId] === valueId && !onlyValue;
    const next = { ...chosen, [optionId]: clearing ? undefined : valueId };

    // Drop any other choice this one just invalidated. Without it, switching to
    // a colour that does not come in the selected size left that size chip
    // drawn as both selected *and* struck through, `selectedVariant` null, the
    // price silently back at the product minimum, and the button reading
    // "Select size" while a size sat visibly selected on screen.
    if (next[optionId]) {
      pickerOptions.forEach((option) => {
        if (option.id === optionId || !next[option.id]) return;
        const stillOffered = variants.some(
          (variant) => (variant.option_values || []).includes(next[option.id])
            && (variant.option_values || []).includes(next[optionId]),
        );
        if (!stillOffered) next[option.id] = undefined;
      });
    }

    setChosen(next);
    if (next[optionId] && isSizeOption(pickerOptions.find((o) => o.id === optionId))) {
      const picked = (pickerOptions.find((o) => o.id === optionId)?.values || [])
        .find((v) => v.id === next[optionId]);
      analytics.selectSize(product, picked?.value);
    }
    const complete = pickerOptions.every((option) => next[option.id]);
    setSelectedVariant(
      complete ? variants.find((variant) => matchesChosen(variant, next)) || null : null,
    );
  };

  // The button names the next step, and it names the *right* one: a piece that
  // only comes in colours was still telling shoppers to select a size.
  const nextChoice = pickerOptions.find((option) => !chosen[option.id]);
  // Nothing left to buy. The size chips already strike themselves through, but
  // the button went on saying "select a size" — an instruction the shopper
  // cannot carry out, on a page that never tells them why. The listing card has
  // said "Satılıb" all along; the detail page owes them the same sentence.
  // Stock 0 on a variant that never depletes (gift cards: manage_inventory
  // off, backorder on) is not "sold out" — the server sells it happily, and
  // the page was the only thing refusing.
  const variantBuyable = (variant) => (variant.stock ?? 0) > 0
    || Boolean(variant.allow_backorder)
    || variant.manage_inventory === false;
  const soldOut = Array.isArray(product.variants)
    && product.variants.length > 0
    && !product.variants.some(variantBuyable);
  let purchaseCtaLabel = t("pdp.add_to_basket", "Add to basket");
  if (soldOut) {
    purchaseCtaLabel = t("product.sold_out", "Satılıb");
  } else if (!selectedVariant) {
    purchaseCtaLabel = nextChoice
      ? `${nextChoice.title} ${t("pdp.choose", "seç")}`
      : t("pdp.select_size", "Select size");
  }
  const inBasket = Boolean(selectedVariant && addedVariantId === selectedVariant.id);
  if (product.is_locked) purchaseCtaLabel = t("pdp.falcon_required", "Falcon access required");
  else if (adding) purchaseCtaLabel = t("pdp.adding", "Adding…");
  else if (inBasket) purchaseCtaLabel = t("pdp.in_basket", "Səbətdə ✓");
  // Never fall back to category_id: it is a UUID, and printing it in the
  // breadcrumb made the storefront look like a half-seeded test environment.
  // The occasion tag was no better — it came from the demo placeholder record,
  // so a t-shirt was filed under "Xarici geyim".
  const category = pickLocale(product.category?.title, locale) || t("cta.collections", "Collections");
  const fitLines = product.fit_description || product.size_guide
    ? [pickLocale(product.fit_description, locale), pickLocale(product.size_guide, locale)].filter(Boolean)
    : [];
  const composition = materialCompositionLabel(product.material_composition) || product.material;
  // Care advice is safety information about a garment, not decoration. Nothing
  // in either feed supplies it, so nothing is printed — a made-up "dry clean"
  // line on a %98 cotton shirt is worse than an absent one.
  const careLines = structuredLocalizedLines(product.care_instructions, locale, []);
  // Retail storefront: the old fallback advertised wholesale terms.
  const deliveryText = pickLocale(product.delivery_info, locale) || policyText(DELIVERY.ribbon, locale);
  const returnText = pickLocale(product.return_info, locale) || policyText(RETURNS.ribbon, locale);
  const fabricOrigin = objectSummary(product.fabric_origin);
  const modelInfo = objectSummary(product.model_info);
  const sizeGuide = product.size_guide;

  const addToBasket = async () => {
    if (!selectedVariant) { setSizeError(true); return; }
    if (!addItem) return;
    setAdding(true);
    setError("");
    try {
      await addItem(selectedVariant.id, 1);
      analytics.addToCart(product, { variantTitle: selectedVariant.title });
      // Yönləndirmə yoxdur: müştəri səhifədə qalır, düymə "Səbətdə ✓" olur
      // və ikinci toxunuş səbətə aparır.
      setAddedVariantId(selectedVariant.id);
    } catch (err) {
      setError(apiErrorMessage(err, t));
    } finally {
      setAdding(false);
    }
  };

  /** CTA seçimsiz basılanda seçim bölməsinə sürüşüb onu işıqlandır. */
  const scrollToOptions = () => {
    setSizeError(true);
    optionsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setOptionsPulse(true);
    window.setTimeout(() => setOptionsPulse(false), 1600);
  };

  /** Hər iki CTA-nın bir davranışı: səbətdədirsə → səbət; seçim tamamdırsa →
      əlavə et; seçim qalıbsa → bölməyə apar. */
  const onPurchaseCta = () => {
    if (inBasket) { navigate("/cart"); return; }
    if (selectedVariant || !nextChoice) { void addToBasket(); return; }
    scrollToOptions();
  };

  const openTryOn = () => {
    analytics.tryOn.open(product, "pdp");
    if (!aiEnabled) return;
    // The fitting is its own page now, not a modal over the product.
    const params = new URLSearchParams({ product: handle });
    if (selectedVariant?.id) params.set("variant", selectedVariant.id);
    if (chosenColourValueId) params.set("color", chosenColourValueId);
    navigate(`/try-on?${params.toString()}`);
  };
  // Persists to the M1 wardrobe API. Guests are sent to sign-in and returned
  // here, so the saved piece survives the detour.
  const saveToWardrobe = async () => {
    if (!authStore.getAccessToken()) {
      navigate(`/auth/login?next=/products/${handle}`);
      return;
    }
    if (saved || savingWardrobe) return;
    setSavingWardrobe(true);
    setError("");
    try {
      await api.post("/me/wardrobe", {
        product_id: product.id,
        variant_id: selectedVariant?.id || null,
      });
      setSaved(true);
      analytics.addToWishlist(product);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, t));
    } finally {
      setSavingWardrobe(false);
    }
  };

  const reservePiece = async () => {
    if (!authStore.getAccessToken()) {
      navigate(`/auth/login?next=/products/${handle}`);
      return;
    }
    if (!selectedVariant) {
      setSizeError(true);
      return;
    }
    setReservationBusy(true);
    setError("");
    try {
      const response = await api.post("/me/reservations", {
        product_id: product.id,
        variant_id: selectedVariant.id,
      });
      analytics.lead("reservation", product);
      setError(`Məhsul ${new Date(response.data.expires_at).toLocaleString("az-AZ")} tarixinədək sizin üçün rezerv edildi.`);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, t));
    } finally {
      setReservationBusy(false);
    }
  };
  const openAvatarPreview = async () => {
    if (!authStore.getAccessToken()) {
      navigate(`/auth/login?next=/products/${handle}`);
      return;
    }
    setAvatarBusy(true);
    setError("");
    try {
      // The saved avatar is a photograph, so it is shown as-is. Dressing it in
      // this product is Try-On's job, not a tint of an illustration.
      const response = await api.get("/me/avatar");
      if (!response.data?.image_url) {
        navigate("/account?tab=avatar");
        return;
      }
      if (avatarPreview?.startsWith("blob:")) URL.revokeObjectURL(avatarPreview);
      setAvatarPreview(mediaUrl(response.data.image_url));
      setAvatarOpen(true);
    } catch (requestError) {
      if (requestError?.response?.status === 404) {
        navigate("/account?tab=avatar");
      } else {
        setError(apiErrorMessage(requestError, t));
      }
    } finally {
      setAvatarBusy(false);
    }
  };
  const scrollToGalleryPanel = (index) => {
    const target = document.getElementById(`pdp-gallery-image-${index}`);
    if (!target) return;
    const track = galleryTrackRef.current;
    if (track && track.scrollWidth > track.clientWidth + 4) {
      // Mobil: block "nearest" səhifəni dik tərpətmir, inline slaydı gətirir.
      // behavior "auto" qəsdəndir: mandatory-snap konteynerində smooth
      // proqram scroll-u yarıda geri snap olunurdu — slayd heç dəyişmirdi.
      target.scrollIntoView({ behavior: "auto", block: "nearest", inline: "start" });
      return;
    }
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="pdp-page" className="adf-pdp-page bg-[var(--adf-cream)] text-[var(--adf-ink)]">
      <SeoMeta
        title={product.seo_title || product.title}
        description={product.seo_description || product.description}
        seo={product.seo || {}}
        locale={locale}
        canonicalPath={`/products/${handle}`}
        image={images[0]}
        // "website" made every product page look like a landing page to a
        // crawler and to every link preview.
        type="product"
        jsonLd={jsonLd}
      />
      <Header />
      <main id="main" data-screen-label="Product page">
        <div className="adf-pdp-breadcrumb adf-container py-5">
          <div className="adf-small text-[var(--adf-gray-2)]">
            <Link to="/" className="hover:text-[var(--adf-ink)]">Home</Link>
            <span className="mx-2">/</span>
            <Link
              to={product.category?.handle ? `/category/${product.category.handle}` : "/shop"}
              className="hover:text-[var(--adf-ink)]"
            >
              {category}
            </Link>
            <span className="mx-2">/</span>
            <span>{title}</span>
          </div>
        </div>

        <section className="adf-pdp-grid" data-testid="pdp-hero">
          <div ref={galleryRailRef} className={`adf-pdp-gallery-rail${images.length === 1 ? " adf-pdp-gallery-rail--single" : ""}`} data-testid="pdp-mobile-gallery">
            <nav className="adf-pdp-thumbs" aria-label={t("pdp.gallery", "Product gallery")}>
              {images.map((src, index) => (
                <button
                  key={`${src}-thumb-${index}`}
                  type="button"
                  onClick={() => scrollToGalleryPanel(index)}
                  aria-label={t("pdp.gallery_image", "Qalereya şəkli {n}", { n: index + 1 })}
                  className="adf-pdp-thumb"
                >
                  <AdfImage source={src} alt="" sizes="68px" />
                </button>
              ))}
            </nav>

            {aiEnabled && galleryTryOnVisible && (
              <TryOnBadge
                onClick={openTryOn}
                variant="full"
                className="adf-pdp-gallery-tryon"
                data-testid="pdp-floating-try-on"
              />
            )}

            {/* Mobil: bu track snap-konteynerdir və rail-in sağ sütununda
                oturur (sol sütun şaquli thumb reylidir). Desktopda
                display:contents ilə şəffafdır — köhnə seçicilər işləyir. */}
            <div ref={galleryTrackRef} className="adf-pdp-track">
            <div id="pdp-gallery-image-0" className="adf-pdp-left" data-testid="pdp-primary-image">
              <div
                className="relative bg-[var(--adf-white)]"
                role="button"
                tabIndex={0}
                aria-label={t("pdp.open_gallery", "Şəkli böyüt")}
                onClick={() => setLightbox(0)}
                onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setLightbox(0); } }}
                data-testid="pdp-open-lightbox"
              >
                {onSale && <span className="adf-pdp-flag" data-testid="pdp-discount-flag">−{discountPct}%</span>}
                <AdfImage
                  source={images[0]}
                  alt={title}
                  priority
                  sizes="(min-width: 1024px) 34vw, 100vw"
                  className={images.length === 1
                    ? "adf-pdp-single-image bg-[var(--adf-white)]"
                    : "block aspect-[3/4] w-full object-cover"}
                />
              </div>
            </div>

            {images.length > 1 && (
            <div className="adf-pdp-middle" data-testid="pdp-gallery">
              {images.slice(1).map((src, index) => (
                <div
                  id={`pdp-gallery-image-${index + 1}`}
                  key={`${src}-${index}`}
                  className="adf-pdp-panel relative bg-[var(--adf-white)]"
                  role="button"
                  tabIndex={0}
                  aria-label={t("pdp.open_gallery", "Şəkli böyüt")}
                  onClick={() => setLightbox(index + 1)}
                  onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setLightbox(index + 1); } }}
                >
                  <AdfImage source={src} alt={`${title} — ${index + 2}`} sizes="(min-width: 1024px) 34vw, 100vw" className="block aspect-[3/4] w-full object-cover" />
                </div>
              ))}
              {/* The Try-On slide is gone from the gallery track. It was one of
                  four entry points to the same feature — the CTA beside the
                  price, the floating badge over the gallery, this slide, and a
                  badge in Complete-the-look — which is a lot of shouting and no
                  hierarchy. It also became a real problem once the mobile
                  gallery turned into a swipe track: a customer paging through
                  product photographs landed on an advertisement between two of
                  them. Discovery stays with the floating badge, intent with the
                  CTA beside the price. */}
              <button
                type="button"
                onClick={() => scrollToGalleryPanel(0)}
                className="adf-pdp-gallery-top"
              >
                <ArrowUp size={15} strokeWidth={1.5} />{t("pdp.back_to_top", "Back to top")}</button>
            </div>
            )}
            </div>
          </div>

          {images.length > 1 && (
            <div className="adf-pdp-swipe-dots" aria-hidden="true" data-testid="pdp-swipe-dots">
              {images.map((src, i) => (
                <span key={`${src}-sd-${i}`} className={i === swipeIndex ? "is-active" : ""} />
              ))}
            </div>
          )}

          <aside className="adf-pdp-buy" data-testid="pdp-info">
            <div className="adf-pdp-card">
              {/* Brand → title with the price set beside it, top-right, the way
                  the reference storefront leads a product. The eyebrow used to
                  read "Italian Craftsmanship" on every piece, in English, on an
                  Azerbaijani page; the category the shopper navigated through
                  says more and is true. */}
              <div className="grid gap-2">
                <span className="adf-pdp-eyebrow">{category}</span>
                <div className="adf-pdp-headrow">
                  <h1 className="adf-pdp-title">{title}</h1>
                  <div className="adf-pdp-price-row" data-testid="pdp-price">
                    {onSale && <span className="adf-pdp-price-was">{formatAZN(compareMinor, locale)}</span>}
                    <span className={`adf-pdp-price${onSale ? " is-sale" : ""}`}>
                      {price || t("pdp.sign_in_pricing", "Sign in for pricing")}
                    </span>
                    {onSale && <span className="adf-pdp-price-off">−{discountPct}%</span>}
                  </div>
                </div>
                {subtitle && <p className="adf-pdp-subtitle">{subtitle}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                {product.made_to_order && <span className="adf-pdp-chip">{t("badge.made_to_order", "Made to order")}</span>}
                {product.limited_edition && <span className="adf-pdp-chip">{t("badge.limited_edition", "Limited edition")}</span>}
                {product.is_new_arrival && <span className="adf-pdp-chip">{t("badge.new_arrival", "New arrival")}</span>}
                {product.outlet && <span className="adf-pdp-chip">{t("badge.outlet", "Outlet")}</span>}
                {product.is_locked && <span className="adf-pdp-chip border-[var(--adf-espresso)] text-[var(--adf-espresso)]">{t("pdp.falcon_access", "Falcon access")}</span>}
              </div>
            </div>

            <div className="adf-pdp-card">
            {/* Every colourless product used to claim to be Graphite. */}
            {product.color && (
              <div className="text-[13px] text-[var(--adf-ink)]">
                <span className="text-[var(--adf-gray-3)]">{t("pdp.colour", "Colour")}: </span>
                {product.color.replace(/_/g, " ")}
              </div>
            )}

            {/* Size and colour are separate decisions and get separate rows.
                Folding them into one grid of variant titles meant a piece in
                three colours showed three buttons all reading "48". */}
            <div
              ref={optionsRef}
              id="pdp-options"
              className={`grid gap-6 ${optionsPulse ? "adf-pdp-options--pulse" : ""}`}
              data-testid="pdp-options"
            >
            {pickerOptions.map((option) => (
              <div key={option.id} className="grid gap-3">
                <div className="flex items-baseline justify-between">
                  <span className="adf-small text-[var(--adf-gray-3)]">{option.title}:</span>
                  {isSizeOption(option) && (
                    <button type="button" onClick={() => { setSizeGuideOpen(true); analytics.openSizeGuide(product); }} className="font-serif text-[15px] text-[var(--adf-gray-2)] underline underline-offset-4">
                      {t("pdp.view_size_guide", "View size guide")}
                    </button>
                  )}
                </div>
                <div className={swatchable(option) ? "adf-pdp-swatch-row" : "adf-pdp-size-grid"}>
                  {option.values.map((value) => {
                    const { exists, inStock } = valueAvailability(option, value);
                    const selected = chosen[option.id] === value.id;
                    // `key` must not travel in the spread — React warns and the
                    // reconciler never sees it.
                    const shared = {
                      type: "button",
                      "data-testid": `option-${slugForTest(option.title)}-${slugForTest(value.value)}`,
                      "aria-pressed": selected,
                      onClick: () => { chooseValue(option.id, value.id); setSizeError(false); },
                      disabled: !exists || !inStock,
                    };
                    // A colour is a thing you look at, not a word you read —
                    // "Açıq qəhvəyi" next to "Qəhvəyi" tells a shopper nothing.
                    if (swatchable(option)) {
                      return (
                        <button
                          key={value.id}
                          {...shared}
                          title={valueLabel(value)}
                          aria-label={valueLabel(value)}
                          className={`adf-pdp-swatch ${selected ? "is-selected" : ""} ${
                            !exists || !inStock ? "is-unavailable" : ""
                          }`}
                        >
                          <span className="adf-pdp-swatch-fill" style={{ background: value.swatch_hex }} />
                        </button>
                      );
                    }
                    return (
                      <button
                        key={value.id}
                        {...shared}
                        className={`h-[48px] border-b border-r border-[var(--adf-gray-5)] px-2 text-[13px] transition ${
                          selected
                            ? "border-[var(--adf-ink)] bg-[var(--adf-ink)] text-[var(--adf-white-warm)]"
                            : "border-[var(--adf-gray-5)] bg-[var(--adf-white)] text-[var(--adf-ink)] hover:border-[var(--adf-ink)]"
                        } ${!exists || !inStock ? "cursor-not-allowed opacity-35 line-through" : ""}`}
                      >
                        {/* Hex-i təyin olunan rəng mətn rejimində də görünsün:
                            15 rəngdən 2-nin swatch-ı olanda bütün sıra mətnə
                            düşür və seçilmiş rənglər «itirdi». */}
                        {value.swatch_hex && (
                          <span className="adf-pdp-chip-dot" style={{ background: value.swatch_hex }} aria-hidden="true" />
                        )}
                        {valueLabel(value)}
                      </button>
                    );
                  })}
                </div>
                {isSizeOption(option) && (() => {
                  // Only when the recommendation is a size this piece actually
                  // comes in. The charts run a continuous scale; a given shirt
                  // may skip 48 entirely, and pointing a customer at a size the
                  // product does not offer is worse than saying nothing.
                  const suggested = sizeHint && (option.values || []).find(
                    (v) => sizeKey(v.value) === sizeKey(sizeHint.recommended_size),
                  );
                  if (!suggested) return null;
                  const { exists, inStock } = valueAvailability(option, suggested);
                  if (!exists || !inStock) return null;
                  return (
                    <button
                      type="button"
                      data-testid="pdp-size-hint"
                      onClick={() => { chooseValue(option.id, suggested.id); setSizeError(false); }}
                      className="justify-self-start text-left text-[12px] leading-5 text-[var(--adf-gray-2)] underline underline-offset-4 hover:text-[var(--adf-ink)]"
                    >
                      {t("pdp.size_hint", "Ölçülərinizə görə")}:{" "}
                      <b className="text-[var(--adf-ink)]">{suggested.value}</b>
                    </button>
                  );
                })()}
                {sizeError && nextChoice?.id === option.id && (
                  <span role="alert" data-testid="pdp-choice-error" className="text-[11px] tracking-[0.04em] text-[#8a3b2e]">
                    {isSizeOption(option)
                      ? t("pdp.select_size_error", "Please select a size")
                      : `${option.title} ${t("pdp.choose", "seç")}`}
                  </span>
                )}
              </div>
            ))}

            {/* Legacy rows that predate real options still have to be buyable. */}
            {!pickerOptions.length && variants.length > 0 && (
              <div className="grid gap-3">
                <span className="adf-small text-[var(--adf-gray-3)]">{t("pdp.size", "Size")}:</span>
                <div className="adf-pdp-size-grid">
                  {variants.map((variant) => (
                    <button
                      key={variant.id}
                      type="button"
                      data-testid={`size-${slugForTest(variant.title)}`}
                      onClick={() => { setSelectedVariant(variant); setSizeError(false); }}
                      disabled={variant.stock === 0 && !variant.allow_backorder && variant.manage_inventory !== false}
                      className={`h-[48px] border-b border-r border-[var(--adf-gray-5)] text-[13px] transition ${
                        selectedVariant?.id === variant.id
                          ? "border-[var(--adf-ink)] bg-[var(--adf-ink)] text-[var(--adf-white-warm)]"
                          : "border-[var(--adf-gray-5)] bg-[var(--adf-white)] text-[var(--adf-ink)] hover:border-[var(--adf-ink)]"
                      } ${variant.stock === 0 && !variant.allow_backorder && variant.manage_inventory !== false ? "cursor-not-allowed opacity-35 line-through" : ""}`}
                    >
                      {variant.title}
                    </button>
                  ))}
                </div>
                {sizeError && <span className="text-[11px] tracking-[0.04em] text-[#8a3b2e]">{t("pdp.select_size_error", "Please select a size")}</span>}
              </div>
            )}
            </div>

            <div className="adf-pdp-primary-actions" data-testid="pdp-actions">
              <button
                type="button"
                data-testid="cta-order-now"
                onClick={onPurchaseCta}
                disabled={adding || product.is_locked}
                className="adf-cta adf-cta-dark adf-pdp-cta w-full disabled:opacity-50"
              >
                {purchaseCtaLabel}
              </button>
              <button
                type="button"
                data-testid="cta-add-to-wardrobe"
                onClick={saveToWardrobe}
                disabled={savingWardrobe}
                aria-label={saved ? t("pdp.wishlist_remove", "Remove from wardrobe") : t("pdp.wishlist_add", "Save to wardrobe")}
                aria-pressed={saved}
                title={saved ? t("pdp.wishlist_remove", "Remove from wardrobe") : t("pdp.wishlist_add", "Save to wardrobe")}
                className={`adf-pdp-wishlist gap-2 disabled:opacity-50 ${saved ? "border-[var(--adf-espresso)] text-[var(--adf-espresso)]" : "border-[var(--adf-ink)] text-[var(--adf-ink)]"}`}
              >
                <Heart size={19} strokeWidth={saved ? 2 : 1.4} fill={saved ? "currentColor" : "none"} />
                {/* Mobildə tək ürək nəhəng boş qutu kimi görünürdü — etiket
                    əməli deyir. Masaüstündə CTA yanında ikon kifayətdir. */}
                <span className="text-[11px] uppercase tracking-[0.1em] md:hidden">
                  {saved
                    ? t("pdp.in_wardrobe", "Qarderobdadır")
                    : t("pdp.wishlist_add_short", "Qarderoba əlavə et")}
                </span>
              </button>
            </div>
            {/* Sahibin eskizi ilə: bir cərgədə geniş WhatsApp + kompakt ürək.
                Nömrə admin-idarəli, Əlaqə səhifəsindəki ilə eynidir. */}
            <div className="adf-pdp-contact-row">
              {whatsappHref && (
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="pdp-whatsapp"
                  className="adf-pdp-tryon-cta adf-pdp-contact-row__wa"
                >
                  <WhatsAppIcon size={18} />
                  {t("pdp.whatsapp_cta", "WhatsApp-da yaz")}
                </a>
              )}
              <button
                type="button"
                onClick={saveToWardrobe}
                disabled={savingWardrobe}
                aria-label={saved ? t("pdp.wishlist_remove", "Remove from wardrobe") : t("pdp.wishlist_add", "Save to wardrobe")}
                aria-pressed={saved}
                className={`adf-pdp-wishlist adf-pdp-wishlist--sq disabled:opacity-50 ${saved ? "border-[var(--adf-espresso)] text-[var(--adf-espresso)]" : "border-[var(--adf-ink)] text-[var(--adf-ink)]"}`}
              >
                <Heart size={19} strokeWidth={saved ? 2 : 1.4} fill={saved ? "currentColor" : "none"} />
              </button>
            </div>
            {/* Eskizdəki qısa xətt — düymələri aşağıdakı məlumatdan ayırır. */}
            <div className="adf-pdp-rule" aria-hidden="true" />
            {error && <div className="rounded-[12px] border border-red-900/20 px-4 py-3 text-sm text-red-900">{error}</div>}

            {/* Try-on belongs beside the decision it informs. It was only a
                badge floating over the gallery, which is not where anyone is
                looking when they are choosing a size. */}
            {aiEnabled && (
              <button
                type="button"
                onClick={openTryOn}
                data-testid="pdp-buy-try-on"
                className="adf-pdp-tryon-cta adf-pdp-tryon-cta--ai"
              >
                <TryOnIcon size={18} aria-hidden="true" />
                {t("pdp.try_on_cta", "AI ilə üzərinizdə görün")}
              </button>
            )}

            {/* Duration, not a delivery date: nothing in this system computes
                one, and an invented date is a promise the atelier has not made. */}
            <p className="adf-pdp-delivery-note">{deliveryText}</p>
            </div>

            {/* Sahibin eskizi: düymələrin altındakı hər şey vahid akkordeon
                barlarıdır — sağda chevron, birincisi açıq. Məhsul hekayəsi də
                ayrıca kart yox, ilk bardır; "Ətraflı" düyməsinin işini indi
                barın özü görür. */}
            <div className="adf-pdp-card adf-pdp-card--flush">
              <div className="adf-pdp-acc-stack">
              {(description || story) && (
                <Detail title={t("pdp.about", "Haqqında")} open testId="pdp-copy">
                  <div className="grid gap-3">
                    {story && <p className="m-0">{story}</p>}
                    {description && <p className="m-0">{description}</p>}
                    {atelierNote && (
                      <p className="m-0 border-l border-[var(--adf-espresso)] pl-4 font-serif text-[15px] italic">{atelierNote}</p>
                    )}
                  </div>
                </Detail>
              )}
              <Detail title={t("pdp.delivery_returns", "Çatdırılma və qaytarma")}>{deliveryText} {returnText}</Detail>
              </div>
            </div>

            {/* SKUs live on variants; the internal UUID is not a product code
                and must never be shown to a shopper. */}
            {(selectedVariant?.sku || product.sku || product.code) && (
              <span className="adf-small text-[var(--adf-gray-3)]">
                {t("pdp.product_code", "Məhsul kodu")} {selectedVariant?.sku || product.sku || product.code}
              </span>
            )}

            <div className="grid gap-[10px]">
              <span className="adf-small text-[var(--adf-gray-3)]">{t("pdp.view_more", "View more")}</span>
              <div className="flex flex-wrap gap-[18px]">
                {/* "Clothing" was the one English word on an Azerbaijani page,
                    and it pointed at the same place as the line above it. */}
                <Link to="/shop" className="font-serif text-[15px] text-[var(--adf-gray-2)] underline underline-offset-4">Atelier Di Falco</Link>
                <Link to="/shop" className="font-serif text-[15px] text-[var(--adf-gray-2)] underline underline-offset-4">{t("pdp.all_products", "Bütün məhsullar")}</Link>
                {/* category_id is a UUID and /category/<uuid> 404s; the handle is
                    what the route actually resolves. */}
                {product.category?.handle && (
                  <Link to={`/category/${product.category.handle}`} className="font-serif text-[15px] text-[var(--adf-gray-2)] underline underline-offset-4">{category}</Link>
                )}
              </div>
            </div>

            {/* Eskizin sonundakı qırıq xətt — alış sütununun bitdiyi yer. */}
            <div className="adf-pdp-end-rule" aria-hidden="true" />
          </aside>
        </section>

        {product.video_embed_url && (
          <section className="adf-container py-16 lg:py-24" data-testid="pdp-video">
            <div className="mx-auto max-w-5xl">
              <SmallCaps>{t("pdp.video", "Product film")}</SmallCaps>
              <div className="mt-6 aspect-video overflow-hidden bg-black">
                <iframe
                  data-testid="pdp-video-frame"
                  src={product.video_embed_url}
                  title={`${pickLocale(product.title, locale)} — ${t("pdp.video", "product film")}`}
                  className="h-full w-full border-0"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          </section>
        )}

        {/* Tək alt xrom: dock-un dizayn dilində üzən ink kapsul. Ad barın
            içindən çıxdı — səhifədə onsuz da var idi və dar zolaqda kəsilirdi;
            qalan yalnız qərar üçün lazım olandır: qiymət, seçim, CTA. */}
        <div className="adf-pdp-mobile-purchase" data-testid="pdp-mobile-sticky-purchase">
          <div className="adf-pdp-mobile-purchase__meta min-w-0">
            <span className="adf-pdp-mobile-purchase__price font-serif">
              {price || t("pdp.price_on_request", "Price on request")}
            </span>
            <span className="adf-pdp-mobile-purchase__variant">
              {selectedVariant?.title || title}
            </span>
          </div>
          <button
            type="button"
            /* By the time this bar appears the size row is far above the fold.
               Sending someone back up to it is the step this replaces: with no
               size chosen the button opens the sizes instead of refusing. */
            onClick={onPurchaseCta}
            disabled={adding || product.is_locked}
            aria-label={`${purchaseCtaLabel}: ${title}`}
            className="adf-cta adf-cta-dark adf-pdp-mobile-purchase__cta disabled:opacity-50"
          >
            {purchaseCtaLabel}
          </button>
        </div>

        <SectionBoundary name="complete-the-look">
          <CompleteLook product={item} related={related} curated={completeLook} onTryOn={openTryOn} onConcierge={() => setConciergeOpen(true)} aiEnabled={aiEnabled} />
        </SectionBoundary>

        <section className="adf-container py-24" data-testid="pdp-related">
          <div className="mb-11">
            <span className="adf-small text-[var(--adf-gray-3)]">{t("pdp.bestsellers_note", "Best sellers from the current season.")}</span>
            <h2 className="adf-heading mt-4 text-[44px]">{t("pdp.also_like", "You May Also Like")}</h2>
          </div>
          <div className="adf-product-grid">
            {explodeColourCards(related.filter((p) => p.handle !== product.handle)).slice(0, 4).map((p, index) => (
              <ProductCard key={p.__cardKey || `${p.id}-${index}`} product={p} index={index} testId="related-product" listName="pdp_related" />
            ))}
          </div>
        </section>
      </main>

      <Footer />
      <GalleryLightbox
        open={lightbox !== null}
        images={images}
        startIndex={lightbox ?? 0}
        title={title}
        onClose={() => setLightbox(null)}
      />

      <TryOnModal
        open={tryOnOpen}
        onClose={() => setTryOnOpen(false)}
        productId={product.id}
        productHandle={handle}
        productTitle={title}
        product={product}
        selectedVariant={selectedVariant}
        currentFit="classic"
        variantId={selectedVariant?.id}
        colourValueId={chosenColourValueId || undefined}
        runtimeConfig={tryOnConfig}
        relatedProducts={related}
        completeLook={completeLook}
      />
      <ConciergeModal
        open={conciergeOpen}
        onClose={() => setConciergeOpen(false)}
        productHandle={handle}
        productTitle={title}
      />
      <AtelierModal
        open={sizeSheetOpen}
        onClose={() => setSizeSheetOpen(false)}
        title={nextChoice ? `${nextChoice.title} ${t("pdp.choose", "seç")}` : t("pdp.select_size", "Ölçü seçin")}
        subtitle={title}
        testid="pdp-size-sheet"
        width="520px"
      >
        <div className="grid gap-6">
          {pickerOptions.map((option) => (
            <div key={option.id} className="grid gap-3">
              <span className="adf-small text-[var(--adf-gray-3)]">{option.title}</span>
              <div className={swatchable(option) ? "adf-pdp-swatch-row" : "adf-pdp-size-grid"}>
                {option.values.map((value) => {
                  const { exists, inStock } = valueAvailability(option, value);
                  const selected = chosen[option.id] === value.id;
                  const common = {
                    type: "button",
                    "aria-pressed": selected,
                    disabled: !exists || !inStock,
                    onClick: () => { chooseValue(option.id, value.id); setSizeError(false); },
                    "data-testid": `sheet-${slugForTest(option.title)}-${slugForTest(value.value)}`,
                  };
                  return swatchable(option) ? (
                    <button
                      key={value.id}
                      {...common}
                      aria-label={valueLabel(value)}
                      title={valueLabel(value)}
                      className={`adf-pdp-swatch ${selected ? "is-selected" : ""} ${!exists || !inStock ? "is-unavailable" : ""}`}
                    >
                      <span className="adf-pdp-swatch-fill" style={{ background: value.swatch_hex }} />
                    </button>
                  ) : (
                    <button
                      key={value.id}
                      {...common}
                      className={`h-[46px] rounded-[10px] border px-2 text-[13px] transition ${
                        selected
                          ? "border-[var(--adf-ink)] bg-[var(--adf-ink)] text-[var(--adf-white-warm)]"
                          : "border-[var(--adf-gray-5)] bg-[var(--adf-white)] text-[var(--adf-ink)]"
                      } ${!exists || !inStock ? "cursor-not-allowed opacity-35 line-through" : ""}`}
                    >
                      {value.swatch_hex && (
                        <span className="adf-pdp-chip-dot" style={{ background: value.swatch_hex }} aria-hidden="true" />
                      )}
                      {valueLabel(value)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <button
            type="button"
            disabled={!selectedVariant || adding}
            onClick={async () => { await addToBasket(); setSizeSheetOpen(false); }}
            data-testid="pdp-size-sheet-add"
            className="adf-cta adf-cta-dark adf-pdp-cta w-full disabled:opacity-50"
          >
            {soldOut
              ? t("product.sold_out", "Satılıb")
              : selectedVariant ? t("pdp.add_to_basket", "Səbətə at") : t("pdp.select_size", "Ölçü seçin")}
          </button>
        </div>
      </AtelierModal>
      <AtelierModal
        open={sizeGuideOpen}
        onClose={() => setSizeGuideOpen(false)}
        title={t("pdp.size_guide", "Size guide")}
        subtitle={title}
        testid="product-size-guide"
      >
        {sizeGuide?.rows?.length
          ? <SizeChart guide={sizeGuide} locale={locale} />
          : <StructuredContent value={sizeGuide} locale={locale} />}
        {product.size_recommendation && (
          <div className="mt-8 border-t border-graphite/15 pt-6">
            <div className="mb-3 text-[11px] uppercase tracking-widest text-graphite/70">{t("pdp.recommendation", "Recommendation")}</div>
            <StructuredContent value={product.size_recommendation} locale={locale} />
          </div>
        )}
      </AtelierModal>
      <AtelierModal
        open={avatarOpen}
        onClose={() => setAvatarOpen(false)}
        title={t("pdp.avatar", "Gentleman Avatar")}
        subtitle={`${title} — ${t("pdp.avatar_subtitle", "personal silhouette preview")}`}
        testid="avatar-product-preview"
        width="560px"
      >
        {avatarPreview && <img loading="lazy" decoding="async" src={avatarPreview} alt={t("pdp.avatar_preview_alt", "Gentleman avatar product preview")} className="w-full border border-espresso/30 bg-stone/15" />}
      </AtelierModal>
    </div>
  );
}

/** One garment-slot vocabulary for the whole page: the chip row in the buy
    column and the styling section below it name the same slot the same way. */
const SLOT_LABELS = {
  az: { jacket: "Üst qat", shirt: "Köynək", trousers: "Şalvar", shoes: "Ayaqqabı", belt: "Kəmər", tie: "Qalstuk", accessory: "Aksesuar" },
  en: { jacket: "Layer", shirt: "Shirt", trousers: "Trousers", shoes: "Shoes", belt: "Belt", tie: "Tie", accessory: "Accessory" },
};

function slotLabel(slot, locale) {
  return SLOT_LABELS[locale === "az" ? "az" : "en"][slot] || "";
}

/** The size row is the one that gets the size-guide link and the "pick one" error. */
// The charts speak one scale and the catalogue two: seeded products label sizes
// "EU 48" while the live Azerbaijani catalogue uses a bare "48". Comparing the
// raw strings matched one and silently missed the other.
function sizeKey(value) {
  return String(value ?? "").trim().toLowerCase().replace(/^eu\s*/, "");
}

function isSizeOption(option) {
  return /ölçü|olcu|size|beden/i.test(option?.title || "");
}

function isColourOption(option) {
  return /rəng|reng|color|colour/i.test(option?.title || "");
}

/**
 * Paint the option rather than name it — but only when every value knows its
 * colour. A row of half swatches and half words is worse than plain words.
 */
function swatchable(option) {
  return Boolean(option?.values?.length) && option.values.every((value) => value.swatch_hex);
}

function slugForTest(value) {
  return String(value ?? "").trim().replace(/\s+/g, "-").toLowerCase();
}

/**
 * The measurement table. A shopper opening the size guide wants to find their
 * own body in a column of numbers — the panel used to answer with the sentence
 * "Choose your usual Italian sartorial size", which only helps the people who
 * did not need to open it.
 */
function SizeChart({ guide, locale }) {
  const columns = guide.columns?.length
    ? guide.columns
    : Object.keys(guide.rows[0] || {}).map((key) => ({ key, az: key, en: key }));
  const heading = (column) => column[locale] || column.az || column.en || column.key;
  return (
    <div className="grid gap-5">
      <div className="adf-size-chart-scroll">
        <table className="adf-size-chart">
          <caption className="sr-only">{pickLocale(guide.note, locale)}</caption>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} scope="col">
                  {heading(column)}
                  {column.key !== "size" && guide.unit ? ` (${guide.unit})` : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {guide.rows.map((row) => (
              <tr key={row.size}>
                {columns.map((column, index) => (
                  index === 0
                    ? <th key={column.key} scope="row">{row[column.key]}</th>
                    : <td key={column.key}>{row[column.key] ?? "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {guide.note && <p className="m-0 text-sm leading-7 text-graphite/75">{pickLocale(guide.note, locale)}</p>}
      {guide.source && <p className="m-0 text-[12px] leading-6 text-graphite/70">{pickLocale(guide.source, locale)}</p>}
    </div>
  );
}

function StructuredContent({ value, locale, empty = "" }) {
  if (!value) return empty ? <p className="text-sm leading-7 text-graphite/75">{empty}</p> : null;
  if (typeof value === "string") return <p className="text-sm leading-7 text-graphite/75">{value}</p>;
  const localized = pickLocale(value, locale);
  if (localized && Object.keys(value).every((key) => ["az", "en"].includes(key))) {
    return <p className="text-sm leading-7 text-graphite/75">{localized}</p>;
  }
  const localizedRows = [...(Array.isArray(value.details) ? value.details : []), ...(Array.isArray(value.items) ? value.items : [])];
  const entries = Object.entries(value).filter(([key]) => !["az", "en", "details", "items"].includes(key));
  return (
    <div className="space-y-4">
      {localized && <p className="text-sm leading-7 text-graphite/75">{localized}</p>}
      {(entries.length > 0 || localizedRows.length > 0) && (
        <dl className="divide-y divide-graphite/10 border-y border-graphite/10">
          {entries.map(([key, item]) => (
            <div key={key} className="grid grid-cols-[minmax(110px,0.7fr)_1.3fr] gap-5 py-4 text-sm">
              <dt className="capitalize text-graphite/70">{key.replace(/_/g, " ")}</dt>
              <dd className="m-0 text-graphite">{formatStructuredValue(item, locale)}</dd>
            </div>
          ))}
          {localizedRows.map((row, index) => (
            <div key={`${row.label || "row"}-${index}`} className="grid grid-cols-[minmax(110px,0.7fr)_1.3fr] gap-5 py-4 text-sm">
              <dt className="capitalize text-graphite/70">{row.label || "Note"}</dt>
              <dd className="m-0 text-graphite">{pickLocale(row, locale) || row.az || row.en || ""}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/* Sahibin eskizindəki bar: ayrıca haşiyəli sətir, sağda açılınca fırlanan
   chevron. +/− cütü getdi — eskiz aşağı-ox istəyir. */
function Detail({ title, children, open = false, testId }) {
  return (
    <details className="adf-pdp-acc" open={open} data-testid={testId}>
      <summary className="adf-pdp-acc__head adf-small text-[var(--adf-ink)]">
        {title}
        <ChevronDown size={18} strokeWidth={1.6} className="adf-pdp-acc__chev" aria-hidden="true" />
      </summary>
      <div className="adf-pdp-acc__body">{children}</div>
    </details>
  );
}

function isUsableImage(url) {
  return Boolean(url) && !String(url).includes("picsum.photos") && !String(url).includes("placehold.co");
}

function materialCompositionLabel(value) {
  if (!value || typeof value !== "object") return "";
  return Object.entries(value)
    .filter(([key]) => !["az", "en", "details", "items"].includes(key))
    .map(([key, val]) => `${String(key).replace(/_/g, " ")} ${val}`)
    .join(" · ");
}

function objectSummary(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return Object.entries(value)
    .filter(([key, item]) => !["az", "en", "details", "items"].includes(key) && item !== null && item !== undefined && item !== "")
    .map(([key, item]) => `${key.replace(/_/g, " ")}: ${formatStructuredValue(item)}`)
    .join(" · ");
}

function structuredLocalizedLines(value, locale, fallback = []) {
  if (!value) return fallback;
  if (typeof value === "string") return [value];
  const lines = [];
  const localized = pickLocale(value, locale);
  if (localized) lines.push(localized);
  const nested = [...(Array.isArray(value.items) ? value.items : []), ...(Array.isArray(value.details) ? value.details : [])];
  nested.forEach((item) => {
    const next = pickLocale(item, locale) || item?.az || item?.en;
    if (next) lines.push(item.label ? `${item.label}: ${next}` : next);
  });
  return lines.length ? lines : fallback;
}

function formatStructuredValue(item, locale = "az") {
  if (item == null) return "";
  if (typeof item !== "object") return String(item);
  return pickLocale(item, locale) || JSON.stringify(item);
}

function flattenCuratedSlots(curated) {
  if (!curated?.slots) return [];
  return Object.entries(curated.slots).flatMap(([slot, rows]) =>
    (rows || []).map((row) => ({ ...row, slot })),
  );
}

function CompleteLook({ product, related, curated, onTryOn, onConcierge, aiEnabled }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [wardrobeState, setWardrobeState] = useState("idle");
  const [note, setNote] = useState("");
  const slotLabels = SLOT_LABELS[locale === "az" ? "az" : "en"];
  const companions = useMemo(
    () => {
      // Curated companions only. This used to fall back to `related`, which is
      // `GET /products?sort=newest` — so a piece with no styled look showed the
      // eight newest products under the heading "Complete the look", and the
      // *same* eight again under "You may also like" a screen further down. Two
      // sections, identical content, neither of them a recommendation.
      //
      // The endpoint does not need the crutch: it already falls back per slot
      // by product type and category with tag matching, so a jacket returns a
      // shirt and trousers rather than whatever arrived last.
      const curatedRows = flattenCuratedSlots(curated);
      const source = curatedRows;
      const seen = new Set();
      return source
        .filter((p) => p.handle !== product.handle)
        .map((p, index) => {
          const item = normalizeProduct(p, index);
          const slot = p.slot || p.curatedSlot || item.product_type || "piece";
          return {
            id: item.id,
            handle: item.handle,
            image: item.thumbnail_url,
            role: slotLabels[slot] || (locale === "az" ? "Parça" : "Piece"),
            name: pickLocale(item.title),
            category: (item.occasion_tags || [])[0] || (locale === "az" ? "Geyim" : "Clothing"),
            material: item.material || (index === 0 ? "Cotton poplin" : "Wool flannel"),
            colorName: item.color || (index === 0 ? "Ivory" : "Mink Beige"),
            price: item.min_price,
          };
        })
        .filter((item) => {
          if (!item.id || seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        })
        .slice(0, 4);
    },
    [curated, product.handle, locale, slotLabels],
  );

  useEffect(() => {
    setWardrobeState("idle");
    setNote("");
  }, [companions, product.id]);

  // No styled look for this piece: say nothing rather than pad the page. The
  // related grid below already covers "what else might I like".
  if (!companions.length) return null;

  // Only real catalogue pieces can be persisted; editorial placeholders have no
  // product row behind them.
  const addWardrobe = async () => {
    if (!companions.length || wardrobeState === "loading") return;
    if (!authStore.getAccessToken()) {
      navigate(`/auth/login?next=/products/${product.handle}`);
      return;
    }
    setNote("");
    setWardrobeState("loading");
    try {
      await Promise.all(companions.map((item) => api.post("/me/wardrobe", { product_id: item.id })));
      analytics.completeTheLook(product, companions.length);
      setWardrobeState("added");
      setNote(`${companions.length} məhsul qarderoba əlavə edildi.`);
    } catch (requestError) {
      setWardrobeState("idle");
      setNote(requestError?.response?.data?.detail || "Qarderoba əlavə edilə bilmədi.");
    }
  };
  const wardrobeLabel = wardrobeState === "loading"
    ? (locale === "az" ? "Əlavə edilir..." : "Adding...")
    : wardrobeState === "added"
      ? (locale === "az" ? "Qarderoba əlavə edildi" : "Added")
      : (locale === "az" ? "Qarderoba əlavə et" : "Add to wardrobe");

  return (
    <section data-testid="pdp-complete-look" className="adf-pdp-complete">
      <div className="adf-container py-12 md:py-16">
        <div className="adf-pdp-complete__head">
          <div>
            <span className="text-[13px] text-[var(--adf-espresso)]">{t("pdp.styling", "Atelier Styling")}</span>
            <h2 className="adf-heading mt-3 text-[38px] leading-none md:text-[52px]">{t("pdp.complete_look", "Görünüşü tamamla")}</h2>
          </div>
          <p className="max-w-xl text-[14px] leading-6 text-[var(--adf-gray-2)]">
            {locale === "az"
              ? "Bu məhsulla uyğunlaşan parçaları seçin və görünüşü tək addımda tamamlayın."
              : "Pick the matching pieces and finish the look in one step."}
          </p>
        </div>

        {companions.length ? (
          <>
            <div className="adf-pdp-complete__grid">
              {companions.map((p) => (
                <article key={p.id} className="group">
                  {/* The photograph is the whole link, so the link needs a name
                      of its own — with alt="" a screen reader announced "link"
                      and nothing else, twice per companion. */}
                  <Link to={`/products/${p.handle}`} aria-label={p.name} className="block aspect-[3/4] overflow-hidden bg-white">
                    <AdfImage source={p.image} alt="" sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.025]" />
                  </Link>
                  <div className="pt-4">
                    <span className="text-[12px] text-[var(--adf-espresso)]">{p.role}</span>
                    <Link to={`/products/${p.handle}`} className="mt-1 block font-serif text-[22px] leading-tight">{p.name}</Link>
                    <p className="mt-2 text-sm text-[var(--adf-gray-2)]">{formatAZN(p.price, locale)}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="adf-pdp-complete__actions">
              <p className="text-sm text-[var(--adf-gray-2)]">
                {companions.length} {locale === "az" ? "uyğun parça" : "matching pieces"}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button onClick={addWardrobe} disabled={wardrobeState === "loading"} className="adf-cta adf-cta-dark disabled:opacity-50">
                  {wardrobeState === "added" ? <><Check size={15} strokeWidth={1.5} /> {wardrobeLabel}</> : wardrobeLabel}
                </button>
                {aiEnabled && <TryOnBadge onClick={onTryOn} variant="full" label={locale === "az" ? "AI ilə sına" : "Try with AI"} className="justify-center" />}
                <button type="button" onClick={onConcierge} className="min-h-11 border-0 bg-transparent px-2 text-sm text-[var(--adf-gray-2)] underline underline-offset-4 transition hover:opacity-60">
                  {t("pdp.styling_advice", "Stil məsləhəti istə")}
                </button>
              </div>
            </div>
            {note && (
              <span className="mt-5 inline-flex items-center gap-2 text-sm leading-6 text-[var(--adf-espresso)]">
                <Check size={14} strokeWidth={1.5} /> {note}
              </span>
            )}
          </>
        ) : (
          <div className="border-y border-[var(--adf-gray-5)] py-8">
            <h3 className="font-serif text-2xl">{locale === "az" ? "Bu məhsul üçün uyğun parçalar hələ əlavə olunmayıb." : "No styling edit yet."}</h3>
            <button type="button" onClick={onConcierge} className="mt-4 min-h-11 text-sm text-[var(--adf-gray-2)] underline underline-offset-4">
              {t("pdp.styling_advice", "Stil məsləhəti istə")}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
